// cleanup_convert_to_typescript_file_PYMTCNTR

import React from 'react';
import moment from 'moment';

import { ACTIONS, CURRENCY_TYPES, DATE_FORMAT, PAYMENT_CATS, WARNING_CODES } from 'constants.js';
import { formatCurrencyAmount, getDecimalPlaces } from 'components/wires/utils/wire.utils';

import { getLoanAlertMessageDetails } from './payments.utils';

const { US_DOLLAR } = CURRENCY_TYPES;

const getSuccessfulDetails = ({ action, fxRates, locale, payment }) => {
  const decimalPlaces =
    payment.currDecimals ?? getDecimalPlaces(payment.currency || US_DOLLAR.currency, fxRates);

  const isForeignAmount = payment?.currency !== US_DOLLAR.currency;

  if (payment.paymentCategory === PAYMENT_CATS.ACH) {
    return [
      {
        head: locale['common.paymentName'],
        value: payment.templateName || payment.batchName || payment.name || ''
      },
      {
        head: locale['common.customerTrace'],
        value: payment.custTraceNum || payment.custTrace
      },
      {
        head: locale['common.transactionDate'],
        value:
          moment(payment.transactionDt).format(DATE_FORMAT) ||
          moment(payment.effectiveEntryDate).format(DATE_FORMAT)
      },
      {
        head: locale['common.paymentType'],
        value: payment.paymentType
      },
      {
        head: locale['common.debits'],
        value: payment.numDebits
      },
      {
        head: locale['common.totalDebits'],
        value: formatCurrencyAmount(payment.totalDebits || 0, decimalPlaces)
      },
      {
        head: locale['common.credits'],
        value: payment.numCredits
      },
      {
        head: locale['common.totalCredits'],
        value: formatCurrencyAmount(payment.totalCredits || 0, decimalPlaces)
      }
    ];
  }
  if (payment.paymentCategory === PAYMENT_CATS.LOAN) {
    return getLoanAlertMessageDetails({ locale, payment });
  }
  if (action === ACTIONS.APPROVE) {
    return [
      {
        head: locale['common.paymentType'],
        value: payment.paymentType
      },
      {
        head: locale['common.payeeName'],
        value: payment.payeeName
      },
      {
        head: locale['common.amount'],
        value: `${formatCurrencyAmount(payment.amount, decimalPlaces)} ${
          isForeignAmount ? payment.currency : ''
        }`
      },
      {
        head: locale['common.transactionDate'],
        value: moment(payment.transactionDt).format(DATE_FORMAT)
      },
      {
        head: locale['common.remainingApproval'],
        value: payment.remainingApproval
      }
    ];
  }
  if (action === ACTIONS.REJECT) {
    return [
      {
        head: locale['common.paymentType'],
        value: payment.paymentType
      },
      {
        head: locale['common.payeeName'],
        value: payment.payeeName
      },
      {
        head: locale['common.amount'],
        value: `${formatCurrencyAmount(payment.amount, decimalPlaces)} ${
          isForeignAmount ? payment.currency : ''
        }`
      },
      {
        head: locale['common.transactionDate'],
        value: moment(payment.transactionDt).format(DATE_FORMAT)
      }
    ];
  }
  return [
    {
      head: locale['common.paymentType'],
      value: payment.paymentType
    },
    {
      head: locale['common.payeeName'],
      value: payment.payeeName
    },
    {
      head: locale['common.amount'],
      value: `${formatCurrencyAmount(payment.amount, decimalPlaces)} ${
        isForeignAmount ? payment.currency : ''
      }`
    }
  ];
};

const getDuplicateDetails = (data) =>
  data.map((row) =>
    Object.keys(row).map((key) => ({
      head: key,
      value: key.includes('Date') && row[key] ? moment(row[key]).format(DATE_FORMAT) : row[key]
    }))
  );

export const getDuplicateMsgDetails = ({ payment }) => {
  let duplicateWarning = {};
  if (payment?.warning) {
    const warningResponse = payment.warningResponse.filter(
      ({ warningCode }) => warningCode === WARNING_CODES.DUPLICATE_PAYMENT_EXISTS
    );
    if (warningResponse.length) {
      duplicateWarning = {
        duplicateMsg: warningResponse[0].warningMessage,
        duplicateDetails: getDuplicateDetails(payment.duplicateAchPayment),
        hasDuplicatePayment: true
      };
    }
  }
  return duplicateWarning;
};

export const getFailedDetails = ({ action, fxRates, locale, payment, reason }) => {
  const decimalPlaces = getDecimalPlaces(payment.currency || US_DOLLAR.currency, fxRates);

  // Wire errors will have reason property and reasonCode (used for locale)
  // ACH errors will have actionSuccess property and actionMessages to display details
  if (payment.paymentCategory === PAYMENT_CATS.ACH) {
    return [
      {
        head: locale['common.paymentName'],
        value: payment.templateName || payment.batchName || payment.name || ''
      },
      {
        head: locale['common.customerTrace'],
        value: payment.custTraceNum || payment.custTrace
      },
      {
        head: locale['common.transactionDate'],
        value:
          moment(payment.transactionDt).format(DATE_FORMAT) ||
          moment(payment.effectiveEntryDate).format(DATE_FORMAT)
      },
      {
        head: locale['common.paymentType'],
        value: payment.paymentType
      },
      {
        head: locale['common.totalDebits'],
        value: formatCurrencyAmount(payment.totalDebits || 0, decimalPlaces)
      },
      {
        head: locale['common.totalCredits'],
        value: formatCurrencyAmount(payment.totalCredits || 0, decimalPlaces)
      },
      {
        head: locale['common.error'],
        value: (
          <div>
            {payment.actionMessages.map((error) => (
              <p key={`userMessage-${error.userMessage}`}>{error.userMessage}</p>
            ))}
          </div>
        )
      }
    ];
  }
  if (action === ACTIONS.APPROVE) {
    return [
      {
        head: locale['common.paymentType'],
        value: payment.paymentType
      },
      {
        head: locale['common.payeeName'],
        value: payment.payeeName
      },
      {
        head: locale['common.amount'],
        value: formatCurrencyAmount(payment.amount, decimalPlaces)
      },
      {
        head: locale['common.transactionDate'],
        value: moment(payment.transactionDt).format(DATE_FORMAT)
      },
      {
        head: locale['common.remainingApproval'],
        value: payment.remainingApproval
      },
      ...(reason
        ? [
            {
              head: locale['common.reason'],
              value: locale[`payments.errors.${reason}`] || reason
            }
          ]
        : [])
    ];
  }
  if (action === ACTIONS.REJECT) {
    return [
      {
        head: locale['common.paymentType'],
        value: payment.paymentType
      },
      {
        head: locale['common.payeeName'],
        value: payment.payeeName
      },
      {
        head: locale['common.amount'],
        value: formatCurrencyAmount(payment.amount, decimalPlaces)
      },
      {
        head: locale['common.transactionDate'],
        value: moment(payment.transactionDt).format(DATE_FORMAT)
      }
    ];
  }
  return [
    {
      head: locale['common.paymentType'],
      value: payment.paymentType
    },
    {
      head: locale['common.payeeName'],
      value: payment.payeeName
    },
    {
      head: locale['common.amount'],
      value: formatCurrencyAmount(payment.amount, decimalPlaces)
    },
    ...(reason
      ? [
          {
            head: locale['common.reason'],
            value: locale[`payments.errors.${reason}`] || reason
          }
        ]
      : [])
  ];
};

export default getSuccessfulDetails;
